import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Card from '../../components/common/Card/Card';
import Button from '../../components/common/Button/Button';
import Modal from '../../components/common/Modal/Modal';
import LoadingSpinner from '../../components/common/LoadingSpinner/LoadingSpinner';
import AppIcon from '../../components/common/AppIcon/AppIcon';
import MentorAvatar from '../../components/common/MentorAvatar/MentorAvatar';
import { useToast } from '../../context/ToastContext';
import { getMentorSlots, bookSession } from '../../services/mentorBookingService';
import { getDefaultTimezone, getTimezoneOptions, getTimezoneLabel } from '../../utils/bookingTimezones';
import { getErrorMessage } from '../../utils/helpers';
import styles from './StudentSelectTime.module.css';

const POLL_INTERVAL_MS = 30000;
const MIN_REFETCH_GAP_MS = 2000;
const NOTES_LIMIT = 500;
const TAKEN_MESSAGE = 'Sorry, that time was just taken. Please choose another.';

const hasSlot = (data, startUtc) => (
  Boolean(data?.days?.some((day) => day.slots.some((slot) => slot.start_utc === startUtc)))
);

const StudentSelectTime = () => {
  const { mentorId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  const [timezone, setTimezone] = useState(getDefaultTimezone);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // The chip the student has picked, and (separately) the snapshot the confirm
  // modal works from, so a 409 can clear the chip without emptying the modal.
  const [selected, setSelected] = useState(null); // { slot, day }
  const [booking, setBooking] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [notes, setNotes] = useState('');
  const [projectId, setProjectId] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [slotTaken, setSlotTaken] = useState(false);

  const requestId = useRef(0);
  const lastFetchAt = useRef(0);
  const modalOpenRef = useRef(false);
  const submitting = useRef(false);
  modalOpenRef.current = modalOpen;

  const fetchSlots = useCallback(async ({ silent = false } = {}) => {
    const thisRequest = ++requestId.current;
    lastFetchAt.current = Date.now();
    if (!silent) {
      setLoading(true);
      setError(false);
    }
    try {
      const result = await getMentorSlots(mentorId, { timezone });
      if (thisRequest !== requestId.current) return;
      setData(result);
      setError(false);
      // A slot that vanished in the background is dropped, unless the confirm
      // modal is open: that path ends in the 409 message instead.
      setSelected((prev) => (
        prev && !hasSlot(result, prev.slot.start_utc) && !modalOpenRef.current ? null : prev
      ));
    } catch (err) {
      if (thisRequest !== requestId.current) return;
      console.error('Error fetching slots:', err);
      if (!silent) setError(true);
    } finally {
      if (thisRequest === requestId.current && !silent) setLoading(false);
    }
  }, [mentorId, timezone]);

  // Initial load, and again whenever mentor or timezone changes.
  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  // Refresh every 30s and on returning to the tab. Hidden tabs do nothing.
  useEffect(() => {
    const refreshIfVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastFetchAt.current < MIN_REFETCH_GAP_MS) return;
      fetchSlots({ silent: true });
    };
    const interval = setInterval(refreshIfVisible, POLL_INTERVAL_MS);
    window.addEventListener('focus', refreshIfVisible);
    document.addEventListener('visibilitychange', refreshIfVisible);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', refreshIfVisible);
      document.removeEventListener('visibilitychange', refreshIfVisible);
    };
  }, [fetchSlots]);

  const handleTimezoneChange = (e) => {
    setSelected(null);
    setTimezone(e.target.value);
  };

  const handleChipClick = (slot, day) => {
    setSelected((prev) => (prev?.slot.start_utc === slot.start_utc ? null : { slot, day }));
  };

  const openConfirm = () => {
    setBooking(selected);
    // One project with this mentor: nothing to choose. Several: the student must pick.
    setProjectId(projects.length === 1 ? projects[0].id : '');
    setSubmitError('');
    setSlotTaken(false);
    setModalOpen(true);
  };

  const closeConfirm = () => {
    if (submitting.current) return;
    setModalOpen(false);
    setSubmitError('');
    setSlotTaken(false);
  };

  const handleConfirm = async () => {
    if (submitting.current || !booking || (projects.length > 0 && !projectId)) return;
    submitting.current = true;
    setSubmitError('');
    try {
      const session = await bookSession({
        mentor_id: mentorId,
        project_id: projectId || undefined,
        start_time_utc: booking.slot.start_utc,
        timezone,
        notes: notes.trim() || undefined
      });
      setModalOpen(false);
      setSelected(null);
      // Both gateways have finished scheduling by the time this resolves (the internal one wrote
      // the calendar event, the external one waited for SYNC), so the message can be definite.
      toast.success(
        session?.booking_gateway === 'internal'
          ? 'Session booked. It is on your mentor\'s calendar — Google has emailed you the invite.'
          : 'Session booked. It will appear in Classes shortly.'
      );
      navigate('/student/classes');
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setSubmitError(TAKEN_MESSAGE);
        setSlotTaken(true);
        setSelected(null);
        fetchSlots({ silent: true });
      } else if (status >= 400 && status < 500) {
        setSubmitError(getErrorMessage(err, 'We could not complete that. Please try again.'));
        if (status === 422) fetchSlots({ silent: true });
      } else if (status === 502) {
        // The slot was free, but the calendar or WhatsApp scheduling failed; it has been released.
        setSubmitError(getErrorMessage(err, 'We could not schedule that session. Please try again.'));
        fetchSlots({ silent: true });
      } else {
        setSubmitError('Something went wrong. Please try again.');
      }
    } finally {
      submitting.current = false;
    }
  };

  const mentor = data?.mentor;
  const projects = mentor?.projects || [];
  const visibleDays = (data?.days || []).filter((day) => day.slots.length > 0);
  const backLink = { to: '/student/book-session', label: '← Back to Mentors' };

  // Nothing to show around yet: first load, or first load failed.
  if (!data) {
    return (
      <div className={styles.container}>
        <h1 className={styles.title}>Select a Time</h1>
        <Link to={backLink.to} className={styles.backLink}>{backLink.label}</Link>
        {loading ? (
          <div className={styles.loading}>
            <LoadingSpinner size="large" message="Loading availability..." />
          </div>
        ) : (
          <div className={styles.errorBox} role="alert">
            <span>Failed to load availability. Please try again.</span>
            <Button size="small" variant="outline" onClick={() => fetchSlots()}>Retry</Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`${styles.container} ${selected ? styles.hasStickyBar : ''}`}>
      <h1 className={styles.title}>Select a Time</h1>
      <Link to={backLink.to} className={styles.backLink}>{backLink.label}</Link>

      <Card className={styles.mentorCard}>
        <div className={styles.mentorHeader}>
          <MentorAvatar mentor={mentor} size={48} />
          <div className={styles.mentorInfo}>
            <h2 className={styles.mentorName}>{mentor.first_name} {mentor.last_name}</h2>
            {mentor.expertise && <p className={styles.mentorExpertise}>{mentor.expertise}</p>}
          </div>
          <span className={styles.sessionLength}>{mentor.session_duration_minutes} min</span>
        </div>
      </Card>

      {data.calendar_synced === false && (
        <div className={styles.warningBox} role="status">
          This mentor's calendar could not be refreshed just now. Times shown may be slightly out of date.
        </div>
      )}

      <div className={styles.timezoneRow}>
        <AppIcon name="clock" size={18} />
        <label htmlFor="timezoneSelect" className={styles.timezoneLabel}>Showing times in:</label>
        <select
          id="timezoneSelect"
          value={timezone}
          onChange={handleTimezoneChange}
          className={styles.timezoneSelect}
        >
          {getTimezoneOptions().map((tz) => (
            <option key={tz.value} value={tz.value}>{tz.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className={styles.loading}>
          <LoadingSpinner size="large" message="Loading availability..." />
        </div>
      ) : error ? (
        <div className={styles.errorBox} role="alert">
          <span>Failed to load availability. Please try again.</span>
          <Button size="small" variant="outline" onClick={() => fetchSlots()}>Retry</Button>
        </div>
      ) : visibleDays.length === 0 ? (
        <Card>
          <div className={styles.empty}>
            <p>No available slots in the next 14 days.</p>
          </div>
        </Card>
      ) : (
        <div className={styles.days}>
          {visibleDays.map((day) => (
            <section key={day.date} className={styles.day} aria-labelledby={`day-${day.date}`}>
              <h2 id={`day-${day.date}`} className={styles.dayHeading}>{day.date_label}</h2>
              <div className={styles.chips}>
                {day.slots.map((slot) => {
                  const isSelected = selected?.slot.start_utc === slot.start_utc;
                  return (
                    <button
                      key={slot.start_utc}
                      type="button"
                      className={`${styles.chip} ${isSelected ? styles.chipSelected : ''}`}
                      aria-pressed={isSelected}
                      onClick={() => handleChipClick(slot, day)}
                    >
                      {slot.start_local}
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {/* Always mounted so screen readers announce the bar when it appears. */}
      <div className={styles.stickyWrap} aria-live="polite">
        {selected && (
          <div className={styles.stickyBar}>
            <div className={styles.stickyText}>
              <span className={styles.stickyLabel}>Selected slot</span>
              <strong className={styles.stickyRange}>
                {selected.slot.start_local} – {selected.slot.end_local}
              </strong>
            </div>
            <Button onClick={openConfirm}>Continue</Button>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={closeConfirm}
        title="Confirm your session"
      >
        {booking && (
          <div className={styles.modalBody}>
            <dl className={styles.summary}>
              <div className={styles.summaryRow}>
                <dt>Mentor</dt>
                <dd>{mentor.first_name} {mentor.last_name}</dd>
              </div>
              <div className={styles.summaryRow}>
                <dt>Date</dt>
                <dd>{booking.day.date_long || booking.day.date_label}</dd>
              </div>
              <div className={styles.summaryRow}>
                <dt>Time</dt>
                <dd>{booking.slot.start_local} – {booking.slot.end_local}</dd>
              </div>
              <div className={styles.summaryRow}>
                <dt>Timezone</dt>
                <dd>{getTimezoneLabel(timezone)}</dd>
              </div>
              <div className={styles.summaryRow}>
                <dt>Duration</dt>
                <dd>{mentor.session_duration_minutes} minutes</dd>
              </div>
              {projects.length === 1 && (
                <div className={styles.summaryRow}>
                  <dt>Project</dt>
                  <dd>{projects[0].project_name || 'Untitled project'}</dd>
                </div>
              )}
            </dl>

            {projects.length > 1 && (
              <div className={styles.notesField}>
                <label htmlFor="sessionProject" className={styles.notesLabel}>
                  Which project is this session for?
                </label>
                <select
                  id="sessionProject"
                  className={styles.projectSelect}
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  disabled={slotTaken}
                  required
                >
                  <option value="">Select a project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.project_name || 'Untitled project'}</option>
                  ))}
                </select>
              </div>
            )}

            <div className={styles.notesField}>
              <label htmlFor="sessionNotes" className={styles.notesLabel}>
                Anything you'd like the mentor to know? (optional)
              </label>
              <textarea
                id="sessionNotes"
                className={styles.notesInput}
                rows={4}
                maxLength={NOTES_LIMIT}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
              />
              <span className={styles.notesCount}>{notes.length}/{NOTES_LIMIT}</span>
            </div>

            {submitError && (
              <div className={styles.inlineError} role="alert">{submitError}</div>
            )}

            <div className={styles.modalActions}>
              <Button variant="outline" onClick={closeConfirm}>
                {slotTaken ? 'Choose another time' : 'Cancel'}
              </Button>
              <Button onClick={handleConfirm} disabled={slotTaken || (projects.length > 0 && !projectId)}>
                Confirm booking
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StudentSelectTime;
